import { useState } from "react";
import { CheckCircle2, ChevronDown, CircleAlert, Lightbulb, TriangleAlert } from "lucide-react";
import { CATEGORY, LEVEL } from "./qualityUi";

const ICON = { error: CircleAlert, warning: TriangleAlert, advice: Lightbulb };
const FILTERS = ["all", "seo", "performance", "accessibility", "content"];

function Item({ it }) {
  const Icon = ICON[it.level];
  const t = LEVEL[it.level][3];
  return (
    <li className="py-2.5" data-testid={`quality-item-${it.code}`}>
      <p className={`flex flex-wrap items-start gap-x-2 text-sm font-medium ${t}`}><Icon size={15} className="mt-0.5 shrink-0" />{it.title}
        <span className="text-xs font-normal text-slate-500">· {CATEGORY[it.category] || it.category}{it.count > 0 && ` · ${it.count} ${it.count === 1 ? "pagina" : "pagine"}`}</span></p>
      <p className="ml-6 mt-0.5 text-xs text-slate-600 break-words">{it.detail}</p>
      {it.pages?.length > 0 && <p className="ml-6 mt-1 flex flex-wrap gap-1">{it.pages.map((p) => <code key={p} className="rounded bg-white px-1.5 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">{p}</code>)}
        {it.count > it.pages.length && <span className="px-1 text-[11px] text-slate-500">+{it.count - it.pages.length}</span>}</p>}
    </li>
  );
}

function Passed({ passed }) {
  const [open, setOpen] = useState(false);
  const total = Object.values(passed || {}).reduce((a, l) => a + l.length, 0);
  if (!total) return null;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3" data-testid="quality-passed">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-emerald-700" data-testid="quality-passed-toggle">
        Superati e ottimizzazioni automatiche · {total}<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && (
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          {["seo", "performance", "accessibility"].map((c) => (
            <div key={c}><p className="mb-1.5 text-xs font-semibold text-emerald-800">{CATEGORY[c]}</p>
              <ul className="space-y-1">{(passed[c] || []).map((p) => <li key={p} className="flex items-start gap-1.5 text-xs text-emerald-800"><CheckCircle2 size={13} className="mt-0.5 shrink-0" />{p}</li>)}</ul></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QualityIssues({ quality }) {
  const [filter, setFilter] = useState("all");
  const items = (quality.items || []).filter((i) => filter === "all" || i.category === filter);
  return (
    <div className="space-y-3" data-testid="quality-issues">
      <div className="flex flex-wrap gap-1.5" data-testid="quality-filters">
        {FILTERS.map((f) => {
          const n = (quality.items || []).filter((i) => f === "all" || i.category === f).length;
          return (
            <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`quality-filter-${f}`}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {f === "all" ? "Tutto" : CATEGORY[f]} · {n}</button>
          );
        })}
      </div>
      {["error", "warning", "advice"].map((lv) => {
        const list = items.filter((i) => i.level === lv);
        if (!list.length) return null;
        const [label, hint, box, t] = LEVEL[lv];
        return (
          <div key={lv} className={`rounded-lg border px-4 py-1 ${box}`} data-testid={`quality-level-${lv}`}>
            <p className={`pt-2 text-[11px] font-semibold uppercase tracking-wider ${t}`}>{label} · {list.length}<span className="ml-2 font-normal normal-case tracking-normal text-slate-500">{hint}</span></p>
            <ul className="divide-y divide-white/70">{list.map((it) => <Item key={it.code} it={it} />)}</ul>
          </div>
        );
      })}
      {!items.length && <p className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-800" data-testid="quality-no-issues">Nessun problema in questa categoria.</p>}
      {(quality.notes || []).map((n) => <p key={n} className="text-xs text-slate-500">{n}</p>)}
      <Passed passed={quality.passed} />
    </div>
  );
}
